import { useEffect, useState } from "react";
import { api, INR, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "./AdminOverview";
import { MessageCircle, Send, X, Search } from "lucide-react";

const STATUSES = ["New","Contacted","In Review","Approved","Disbursed","Rejected"];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get("/leads", { params: statusFilter ? { status: statusFilter } : {} });
    setLeads(data);
  };
  useEffect(() => { load(); }, [statusFilter]);

  const filtered = leads.filter(l => !filter || l.name.toLowerCase().includes(filter.toLowerCase()) || l.phone.includes(filter));

  return (
    <div className="p-8 lg:p-12" data-testid="admin-leads">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl">Leads</h1>
          <p className="text-black/50 mt-1">Manage all loan applications & lead follow-ups.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40"/>
            <input data-testid="leads-search" placeholder="Search name or phone" value={filter} onChange={(e)=>setFilter(e.target.value)}
              className="pl-9 pr-3 py-2 rounded-lg border border-[#EAEAEA] text-sm bg-white focus:border-[#D4AF37] outline-none"/>
          </div>
          <select data-testid="leads-status-filter" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} className="border border-[#EAEAEA] rounded-lg text-sm py-2 px-3 bg-white">
            <option value="">All statuses</option>
            {STATUSES.map(s=><option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-8 bg-white border border-[#EAEAEA] rounded-xl overflow-hidden">
        <table className="w-full text-sm sk-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-black/50 bg-black/[0.02]">
              <th className="py-3 px-4">Applicant</th><th>Loan</th><th>Amount</th><th>City</th><th>Status</th><th>Created</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(l => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-[#EEE] hover:bg-black/[0.02] cursor-pointer" onClick={()=>setSelected(l)}>
                <td className="py-3 px-4">
                  <div className="font-medium">{l.name}</div>
                  <div className="text-xs text-black/50">{l.phone}</div>
                </td>
                <td>{l.loan_type}</td>
                <td>{INR(l.amount)}</td>
                <td>{l.city || "—"}</td>
                <td><StatusBadge s={l.status}/></td>
                <td className="text-xs text-black/50">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="pr-4"><button className="text-[#8A6E1E] hover:underline text-xs">View</button></td>
              </tr>
            ))}
            {filtered.length===0 && <tr><td colSpan="7" className="py-12 text-center text-black/40">No leads found</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && <LeadDrawer leadId={selected.id} onClose={()=>{ setSelected(null); load(); }}/>}
    </div>
  );
}

function LeadDrawer({ leadId, onClose }) {
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");
  const [smsMsg, setSmsMsg] = useState("Hi, this is SK Group Financial Services regarding your loan application.");
  const load = async () => { const { data } = await api.get(`/leads/${leadId}`); setLead(data); };
  useEffect(() => { load(); }, [leadId]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const changeStatus = async (status) => {
    try { await api.put(`/leads/${leadId}`, { status }); toast.success(`Status: ${status}`); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/leads/${leadId}/notes`, { text: note }); setNote(""); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const sendMsg = async (channel) => {
    try { await api.post("/messages/send", { channel, phone: lead.phone, message: smsMsg }); toast.success(`${channel.toUpperCase()} sent (mocked)`); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (!lead) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose}>
      <div data-testid="lead-drawer" className="w-full max-w-xl bg-white h-full overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
        <div className="p-6 border-b border-[#EAEAEA] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-black/50">Lead #{lead.id.slice(0,8).toUpperCase()}</div>
            <div className="font-display text-2xl">{lead.name}</div>
          </div>
          <button data-testid="lead-drawer-close" onClick={onClose} className="p-2 hover:bg-black/5 rounded-lg"><X size={18}/></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Info label="Phone" v={lead.phone}/>
            <Info label="Email" v={lead.email||"—"}/>
            <Info label="Loan type" v={lead.loan_type}/>
            <Info label="Amount" v={INR(lead.amount)}/>
            <Info label="Tenure" v={`${lead.tenure_months} mo`}/>
            <Info label="Monthly income" v={lead.monthly_income?INR(lead.monthly_income):"—"}/>
            <Info label="City" v={lead.city||"—"}/>
            <Info label="Message" v={lead.message||"—"}/>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-black/50 mb-2">Status</div>
            <div className="flex gap-2 flex-wrap">
              {STATUSES.map(s => (
                <button key={s} data-testid={`status-btn-${s}`} onClick={()=>changeStatus(s)}
                  className={`sk-badge border transition-colors ${lead.status===s?"bg-[#D4AF37] text-black border-[#D4AF37]":"bg-white border-[#EAEAEA] text-black/70 hover:border-[#D4AF37]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-black/50 mb-2">Send message</div>
            <textarea rows="3" className="w-full border border-[#EAEAEA] rounded-lg p-3 text-sm bg-white focus:border-[#D4AF37] outline-none"
              value={smsMsg} onChange={(e)=>setSmsMsg(e.target.value)}/>
            <div className="mt-2 flex gap-2">
              <button data-testid="send-sms" onClick={()=>sendMsg("sms")} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#EAEAEA] text-sm hover:border-[#D4AF37]"><Send size={14}/> SMS</button>
              <button data-testid="send-whatsapp" onClick={()=>sendMsg("whatsapp")} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm hover:bg-emerald-700"><MessageCircle size={14}/> WhatsApp</button>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-black/50 mb-2">Notes & history</div>
            <div className="flex gap-2">
              <input value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Add a note..." data-testid="note-input"
                className="flex-1 border border-[#EAEAEA] rounded-lg px-3 py-2 text-sm bg-white focus:border-[#D4AF37] outline-none"/>
              <button data-testid="add-note" onClick={addNote} className="px-4 py-2 rounded-lg bg-[#0B0B0B] text-white text-sm">Add</button>
            </div>
            <div className="mt-4 space-y-3">
              {(lead.notes||[]).map(n => (
                <div key={n.id} className={`p-3 rounded-lg ${n.system?"bg-[#D4AF37]/10 border border-[#D4AF37]/25":"bg-black/5"}`}>
                  <div className="text-sm">{n.text}</div>
                  <div className="text-xs text-black/50 mt-1">{n.author} · {new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))}
              {(!lead.notes || lead.notes.length===0) && <div className="text-black/40 text-sm text-center py-4">No notes yet</div>}
            </div>
          </div>

          {(lead.documents||[]).length>0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-black/50 mb-2">Documents</div>
              <div className="space-y-2">
                {lead.documents.map(d => (
                  <div key={d.id} className="text-sm bg-black/5 rounded-lg p-3 flex justify-between">
                    <div><div className="font-medium">{d.name}</div><div className="text-xs text-black/50">{d.doc_type}</div></div>
                    <div className="text-xs text-black/50">{new Date(d.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function Info({ label, v }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-black/50">{label}</div>
      <div className="text-sm font-medium mt-0.5">{v}</div>
    </div>
  );
}
